import { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isAndroid, isChrome, isMobileOnly, isSafari } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ChannelContext, ConfigContext } from '../../context';
import { organisationApi, OrganisationDownloadAppLinkRequestForUserPayload } from '../../services';
import {
  Button,
  ButtonType,
  DeeplinkType,
  getDeepLinkUrl,
  IconLabel,
  Modal,
  useDeepLink,
} from '../../shared';

import classes from './DownloadAppModal.module.scss';

export const DownloadAppModal: FunctionComponent = () => {
  const { t } = useTranslation();

  const location = useLocation();

  const referralCode = new URLSearchParams(location.search).get('referralCode');

  const { appIconUrl, appName } = useContext(ConfigContext).config.elements.deeplink;

  const { id: channelId = -1 } = { ...useContext(ChannelContext).channel };

  const { isDeepLink } = useDeepLink();

  const [organisationDownloadAppLinkRequestForUser] =
    organisationApi.endpoints.organisationDownloadAppLinkRequestForUser.useMutation();

  const [isOpen, setIsOpen] = useState<boolean | undefined>();

  const [appLink, setAppLink] = useState<
    OrganisationDownloadAppLinkRequestForUserPayload | undefined
  >();

  const init = useCallback(async () => {
    const appLink = await organisationDownloadAppLinkRequestForUser({
      channelId,
      referralCode,
    }).unwrap();

    if (!appLink) {
      return;
    }

    if (referralCode && isAndroid) {
      window.location.replace(appLink.link);
      return;
    }

    setAppLink(appLink);
  }, [channelId, organisationDownloadAppLinkRequestForUser, referralCode]);

  useEffect(() => {
    if (!isMobileOnly || !isDeepLink) {
      return;
    }

    init();
  }, [init, isDeepLink]);

  useEffect(() => {
    if (appLink?.link && typeof isOpen === 'undefined') {
      setIsOpen(true);
    }
  }, [appLink?.link, isOpen]);

  const iconId = useMemo(() => {
    switch (true) {
      case isChrome:
        return 'chrome';
      case isSafari:
        return 'safari';
      default:
        return 'browser-default';
    }
  }, []);

  const label = useMemo(() => {
    switch (true) {
      case isChrome:
        return 'Chrome';
      case isSafari:
        return 'Safari';
      default:
        return 'Browser';
    }
  }, []);

  const installHandler = useCallback(async () => {
    if (!appLink?.link) {
      return;
    }

    if (referralCode) {
      await navigator.clipboard.writeText(
        getDeepLinkUrl({ type: DeeplinkType.REFERRAL, referralCode })
      );
    }

    window.location.replace(appLink.link);
  }, [appLink?.link, referralCode]);

  const header = t('downloadAppModal.header');

  const body = useMemo(() => {
    return (
      <div className={classes['download-app-modal']}>
        <div className={classes['download-app-modal__header']}>{header}</div>
        <div className={classes['download-app-modal__option']}>
          <div className={classes['download-app-modal__option-app']}>
            <img width={24} height={24} src={appIconUrl} alt={''} />
            <IconLabel label={appName} nonClickable />
          </div>
          <Button
            className={classes['download-app-modal__option-button']}
            type={ButtonType.secondary}
            label={t('common.install')}
            onClick={installHandler}
            fullWidth
          />
        </div>
        {!referralCode && (
          <div className={classes['download-app-modal__option']}>
            <IconLabel iconId={iconId} label={label} nonClickable />
            <Button
              className={classes['download-app-modal__option-button']}
              type={ButtonType.secondary}
              label={t('common.continue')}
              onClick={() => setIsOpen(false)}
              fullWidth
            />
          </div>
        )}
      </div>
    );
  }, [appIconUrl, appName, header, iconId, installHandler, label, referralCode, t]);

  return <Modal isOpen={Boolean(isOpen)} body={body} ariaLabel={header} alignBottom keepOpened />;
};
